import {
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { OnboardingPageProgress } from '../../components/onboarding/onboarding-page-progress/onboarding-page-progress';
import { OnboardingPreview } from '../../components/onboarding/onboarding-preview/onboarding-preview';
import { UserService } from '../../../core/services/user/user.service';
import { User } from '../../../core/entities/user/user.entity';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CitySelector } from '../../../core/components/city-selector/city-selector';
import { ImageUploadComponent } from '../../../core/components/image-upload/image-upload.component';
import { ToastrService } from 'ngx-toastr';
import { TagsSelector } from '../../../core/components/tags-selector/tags-selector';
import {Router} from '@angular/router';
import { ServiceService } from '../../services/service/service.service';
import { Subject, finalize, takeUntil } from 'rxjs';
import confetti from 'canvas-confetti';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';

enum Step {
  Intro = 0,
  Profile = 1,
  Service = 2,
  Finish = 3,
}

@Component({
  selector: 'app-onboarding',
  imports: [
    TranslocoPipe,
    OnboardingPageProgress,
    OnboardingPreview,
    ReactiveFormsModule,
    CitySelector,
    ImageUploadComponent,
    TagsSelector,
  ],
  templateUrl: './onboarding.html',
  standalone: true,
  styleUrl: './onboarding.scss',
})
export class Onboarding implements OnInit, OnDestroy {
  public user: User | null = null;

  // Reactive forms
  public profileForm!: FormGroup;
  public serviceForm!: FormGroup;

  // Service context
  public numberOfOnlineServices = 0;
  public serviceImageFile: File | null = null;

  // Loading flags
  public savingProfile = false;
  public creatingService = false;

  // Step state
  public currentStep: Step = Step.Intro;

  // Teardown
  private readonly destroy$ = new Subject<void>();


  public constructor(
    private readonly userService: UserService,
    private readonly serviceService: ServiceService,
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
  ) {
    // Step 1 (Profile)
    this.profileForm = this.fb.group({
      firstname: ['', [Validators.required, Validators.maxLength(60)]],
      biography: ['', [Validators.required, Validators.maxLength(300)]],
      city: ['', Validators.required],
      tags: [[], Validators.required], // Important: tags is an array
    });

    // Step 2 (Service)
    this.serviceForm = this.fb.group({
      serviceName: ['', [Validators.required, Validators.maxLength(80)]],
      serviceDescription: ['', [Validators.required, Validators.maxLength(500)]],
      serviceDuration: [60, [Validators.required, Validators.min(1)]],
      servicePrice: [null, [Validators.required, Validators.min(1)]],
      serviceOnline: [true],
      serviceAdvantages: [[]],
    });
  }

  // Convenience getters
  get firstName() { return this.profileForm.get('firstname')!; }
  get serviceName() { return this.serviceForm.get('serviceName')!; }
  get servicePrice() { return this.serviceForm.get('servicePrice')!; }

  // ---------- Lifecycle ----------

  public ngOnInit(): void {
    this.currentStep = Step.Intro;

    this.userService.getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.user = user;
          this.profileForm.patchValue({
            firstname: user?.firstname ?? '',
            biography: user?.biography ?? '',
            city: user?.city ?? '',
            tags: Array.isArray(user?.tags) ? user.tags : (user?.tags ? [user.tags] : []),
          }, { emitEvent: false });

          if (!this.isStep1Complete()) {
            this.goTo(Step.Profile); return;
          }

          this.serviceService.getServices()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (services) => {
                this.numberOfOnlineServices = (services || []).filter(s => s.online).length;
                this.goTo(this.numberOfOnlineServices > 0 ? Step.Finish : Step.Service);
              },
              error: () => this.goTo(Step.Service)
            });
        },
        error: () => this.goTo(Step.Profile)
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(); this.destroy$.complete();
  }

  // ---------- State machine ----------

  /**
   * Guarded navigation to a target step.
   * All step-entry side effects (like animations) are handled here.
   */
  public goTo(step: Step): void {
    switch (step) {
      case Step.Profile:
        this.currentStep = Step.Profile;
        return;

      case Step.Service:
        if (!this.isStep1Complete()) {
          this.showErrorToast('onboarding.errors.step1.incomplete');
          this.currentStep = Step.Profile;
          return;
        }
        this.currentStep = Step.Service;
        return;

      case Step.Finish:
        if (!this.isStep1Complete()) {
          this.showErrorToast('onboarding.errors.step1.incomplete');
          this.currentStep = Step.Profile;
          return;
        }
        if (!this.isStep2Complete()) {
          this.showErrorToast('onboarding.errors.step2.incomplete');
          this.currentStep = Step.Service;
          return;
        }
        this.currentStep = Step.Finish;
        this.fireConfetti();
        return;

      case Step.Intro:
      default:
        this.currentStep = Step.Intro;
        return;
    }
  }

  // ---------- Completion rules (single source of truth) ----------

  private isStep1Complete(): boolean {
    const { firstname, city, tags, biography } = this.profileForm.value;
    const validTags = Array.isArray(tags) ? tags.length > 0 : !!tags;
    // Also check form status to catch maxlength/min constraints
    return this.profileForm.valid && !!firstname && !!city && validTags && !!biography;
  }

  private isStep2Complete(): boolean {
    if (this.numberOfOnlineServices > 0) return true;
    // If you specifically need the service to be "online" to finish onboarding,
    // enforce it here; otherwise rely on form validity only.
    const { serviceOnline } = this.serviceForm.value;
    return this.serviceForm.valid && !!serviceOnline;
  }

  // ---------- Step 1 actions ----------

  public publishProfile(): void {
    if (this.savingProfile || this.profileForm.invalid) return;

    this.savingProfile = true;
    const { firstname, city, tags, biography } = this.profileForm.value;

    this.userService.updateUser(
      firstname, null, true, city, tags, biography, null, null,
    ).pipe(
      finalize(() => this.savingProfile = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.showSuccessToast('public-profile.identity.saved');
        // Move forward if allowed
        this.goTo(Step.Service);
      },
      error: () => {
        this.showErrorToast('public-profile.identity.saved-error');
      }
    });
  }

  // ---------- Step 2 actions ----------

  public onServiceImageSelected(files: File[]): void {
    // Keep the chosen image locally to send with the API call
    if (!files || files.length !== 1) return;
    this.serviceImageFile = files[0];
  }

  public createFirstService(): void {
    if (this.creatingService || this.serviceForm.invalid) return;

    this.creatingService = true;

    const {
      serviceName,
      serviceDescription,
      serviceDuration,
      servicePrice,
      serviceOnline,
      serviceAdvantages
    } = this.serviceForm.value;

    // Convert to API types
    const name: string = String(serviceName);
    const price: number = Number(servicePrice);
    const online: boolean = Boolean(serviceOnline);
    const description: string | null = serviceDescription ? String(serviceDescription) : null;
    const duration: number = Number(serviceDuration);
    const advantages: string[] = Array.isArray(serviceAdvantages) ? serviceAdvantages : [];

    this.serviceService.createService(
      name,
      description,
      duration,
      price,
      false,
      null,
      null,
      false,
      null,
      online,
      false,
      null,
      advantages,
      this.serviceImageFile
    ).pipe(
      finalize(() => this.creatingService = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.toastr.success(
          this.transloco.translate('onboarding.step2.success.content'),
          this.transloco.translate('onboarding.step2.success.title')
        );

        // After creation, we consider step 2 complete → go to finish
        this.goTo(Step.Finish);
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('onboarding.step2.error.content'),
          this.transloco.translate('onboarding.step2.error.title')
        );
      }
    });
  }

  // ---------- Toast helpers ----------

  public showSuccessToast(translationKey: string): void {
    this.toastr.success(
      this.transloco.translate(`${translationKey}.content`),
      this.transloco.translate(`${translationKey}.title`)
    );
  }

  public showErrorToast(translationKey: string): void {
    this.toastr.error(
      this.transloco.translate(`${translationKey}.content`),
      this.transloco.translate(`${translationKey}.title`)
    );
  }

  // ---------- Profile image upload ----------

  public uploadProfilePicture(files: File[]): void {
    if (!files || files.length !== 1) return;
    const file = files[0];

    try {
      this.userService.updateUserImage(file)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (updatedUser) => {
            this.user = updatedUser;
            this.toastr.success(
              this.transloco.translate('public-profile.identity.image.success.content'),
              this.transloco.translate('public-profile.identity.image.success.title')
            );
          },
          error: () => {
            this.toastr.error(
              this.transloco.translate('public-profile.identity.image.error.content'),
              this.transloco.translate('public-profile.identity.image.error.title')
            );
          }
        });
    } catch (error) {
      this.toastr.error(
        this.transloco.translate('public-profile.identity.image.error.content'),
        this.transloco.translate('public-profile.identity.image.error.title')
      );
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  public fireConfetti(): void {
    setTimeout(() => {
      confetti({
        particleCount: 200, // Number of confetti particles
        spread: 180, // How far the confetti spreads
        origin: { y: 0.6 }, // Where the confetti starts
        colors: ['#009900', '#056432', '#047c3c', '#6fd89b'],
        scalar: 1.2, // Adjust the size of the particles
      });
    }, 1000); // Delay to allow the page animation to complete
  }

  public goToDashboard(): void {
    this.authenticationService.refreshToken().subscribe(
      () => {
        this.router.navigate(['/manage/dashboard']);
      }, () => {
        this.authenticationService.logout();
        this.router.navigate(['/login']);
      }
    );
  }

  protected readonly Step = Step;
}
